import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import MailOutlineIcon from '@mui/icons-material/MailOutlined';
import LoginIcon from '@mui/icons-material/Login';
import GoogleIcon from '@mui/icons-material/Google';

import { isMarchandViveretHost, loginAndStoreGrampsToken, startOidcLogin } from './grampsAuth';
import { getDefaultTreeName, KNOWN_TREES } from './treeName';
import { useOidcProviders } from './hooks/useOidcProviders';
import { getViewPath } from './viewRoute';

// --- Contenu de la page : à personnaliser librement ---
const CONTENT = {
  eyebrow: 'Généalogie & histoire familiale',
  title: 'Genealhistory',
  subtitle: "Explorez plusieurs générations d'histoire familiale : arbres, actes, lieux et récits réunis en un seul endroit.",
  pitchParagraphs: [
    "Depuis plusieurs années, je rassemble, recoupe et mets en forme les archives de familles entières : actes d'état civil, recensements, correspondances et photographies.",
    "Ce site présente le résultat de ce travail sous une forme vivante et navigable, pensée pour donner envie d'aller plus loin dans l'histoire de sa propre famille.",
  ],
  highlights: [
    'Arbres généalogiques explorables sur plusieurs siècles',
    'Événements, occupations et lieux de vie replacés dans leur contexte',
    "Recherches sur mesure à partir d'une simple demande",
  ],
  contactHeadline: 'Une question, une demande de recherche, une envie de collaborer ?',
  contactBody: "Que vous ayez des archives à partager, une branche familiale à explorer ou simplement une question, n'hésitez pas à me contacter directement.",
  contactEmail: 'jerome.viveret@gmail.com',
};

// Variante affichée aux visiteurs de marchand-viveret.eu, à l'occasion de la
// cousinade Viveret du 26 septembre 2026 : même mise en page, message d'accueil dédié.
const COUSINADE_CONTENT = {
  eyebrow: 'Cousinade Viveret · 26 septembre 2026',
  title: 'Bienvenue à la famille Viveret',
  subtitle: "Vous êtes venu(e) à la cousinade, ou vous portez le nom Viveret ? Cet arbre généalogique retrace plusieurs générations de la famille : découvrez-y vos ancêtres, vos cousins, et les liens qui vous relient à tous ceux présents le 26 septembre.",
  pitchParagraphs: [
    "Ce site rassemble les actes d'état civil, recensements, correspondances et photographies retrouvés au fil des recherches sur la famille Viveret.",
    "Que vous soyez déjà venu(e) à la cousinade ou que vous découvriez la famille pour la première fois, l'arbre est ouvert à l'exploration : cherchez votre nom, remontez vos branches, retrouvez vos cousins.",
  ],
  highlights: [
    'Arbres généalogiques explorables sur plusieurs siècles',
    'Événements, occupations et lieux de vie replacés dans leur contexte',
    "Recherches sur mesure à partir d'une simple demande",
  ],
  contactHeadline: 'Une photo, un souvenir, une branche à compléter ?',
  contactBody: "Si vous avez des archives à partager ou une question sur votre place dans l'arbre, n'hésitez pas à me contacter directement.",
  contactEmail: 'jerome.viveret@gmail.com',
};

export function LandingPage() {
  const navigate = useNavigate();
  const isCousinadeHost = isMarchandViveretHost(window.location.hostname);
  const content = isCousinadeHost ? COUSINADE_CONTENT : CONTENT;
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const oidcProviders = useOidcProviders();
  const googleProvider = oidcProviders.find((provider) => provider.id === 'google');
  const defaultTreeName = getDefaultTreeName();
  const [googleTreeUuid, setGoogleTreeUuid] = useState(
    KNOWN_TREES.find((tree) => tree.id === defaultTreeName)?.uuid ?? KNOWN_TREES[0].uuid,
  );

  const handleExplore = () => {
    if (isCousinadeHost) {
      navigate(getViewPath('ascendency', undefined, undefined, 'viveret'));
      return;
    }
    navigate(`/${encodeURIComponent(getDefaultTreeName())}`);
  };

  const handleLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const token = await loginAndStoreGrampsToken(username, password, rememberMe ? 'local' : 'session');
      if (!token) {
        setErrorMessage('Connexion impossible. Vérifiez votre identifiant et votre mot de passe.');
        return;
      }
      navigate(`/${encodeURIComponent(getDefaultTreeName())}`);
    } catch {
      setErrorMessage('La connexion a échoué. Réessayez dans quelques instants.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    if (!googleProvider) return;
    startOidcLogin(googleProvider.loginUrl, googleTreeUuid, rememberMe ? 'local' : 'session');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #fff7ed 0%, #ffffff 45%)',
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          px: { xs: 2, sm: 4 },
          py: { xs: 5, sm: 8 },
        }}
      >
        <Stack spacing={1} sx={{ alignItems: 'flex-start', mb: 6 }}>
          <Typography
            variant="overline"
            sx={{ color: '#d97706', fontWeight: 700, letterSpacing: 1.5 }}
          >
            {content.eyebrow}
          </Typography>
          <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '3rem' } }}>
            {content.title}
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 640, fontWeight: 400 }}>
            {content.subtitle}
          </Typography>

          <Button
            onClick={handleExplore}
            variant="contained"
            size="large"
            startIcon={<PlayArrowRoundedIcon />}
            sx={{
              mt: 2,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '999px',
              px: 3,
              py: 1.2,
              background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
              boxShadow: '0 8px 20px rgba(239, 68, 68, 0.30)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d97706 0%, #dc2626 100%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 10px 22px rgba(220, 38, 38, 0.38)',
              },
            }}
          >
            Explorer la généalogie
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ alignItems: 'flex-start' }}>
          <Stack spacing={3} sx={{ flex: 1.3 }}>
            {content.pitchParagraphs.map((paragraph) => (
              <Typography key={paragraph} variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.7 }}>
                {paragraph}
              </Typography>
            ))}

            <Stack spacing={1.2} sx={{ mt: 1 }}>
              {content.highlights.map((highlight) => (
                <Stack key={highlight} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      mt: '0.5em',
                      width: 8,
                      height: 8,
                      flexShrink: 0,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
                    }}
                  />
                  <Typography variant="body1">{highlight}</Typography>
                </Stack>
              ))}
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fff7ed 0%, #fef2f2 100%)',
                border: '1px solid rgba(217, 119, 6, 0.25)',
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {content.contactHeadline}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {content.contactBody}
                </Typography>
                <Button
                  component="a"
                  href={`mailto:${content.contactEmail}`}
                  variant="contained"
                  size="large"
                  startIcon={<MailOutlineIcon />}
                  sx={{
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '999px',
                    px: 3,
                    background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)',
                    },
                  }}
                >
                  {content.contactEmail}
                </Button>
              </Stack>
            </Paper>
          </Stack>

          {!isCousinadeHost && (
            <Paper
              elevation={3}
              component="form"
              onSubmit={handleLogin}
              sx={{
                flex: 1,
                width: '100%',
                maxWidth: { xs: '100%', md: 380 },
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 3,
                position: { md: 'sticky' },
                top: { md: 24 },
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <LoginIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Déjà un accès ?
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Connectez-vous pour retrouver votre arbre familial.
                </Typography>
                <TextField
                  label="Identifiant"
                  placeholder="Exemple : jerome-..."
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  fullWidth
                  required
                  size="small"
                />
                <TextField
                  label="Mot de passe"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  fullWidth
                  required
                  size="small"
                />
                <FormControlLabel
                  control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} size="small" />}
                  label="Rester connecté sur cet appareil"
                />
                {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || !username.trim() || !password}
                  sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}
                >
                  {isSubmitting ? <CircularProgress size={18} color="inherit" /> : <span>Se connecter</span>}
                </Button>
                {googleProvider && <>
                  <Divider>ou</Divider>
                  <FormControl fullWidth size="small">
                    <InputLabel id="google-tree-select-label">Arbre familial</InputLabel>
                    <Select
                      labelId="google-tree-select-label"
                      label="Arbre familial"
                      value={googleTreeUuid}
                      onChange={(event) => setGoogleTreeUuid(event.target.value)}
                    >
                      {KNOWN_TREES.map((tree) => <MenuItem key={tree.uuid} value={tree.uuid}>{tree.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Button
                    onClick={handleGoogleLogin}
                    variant="outlined"
                    startIcon={<GoogleIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px' }}
                  >
                    Continuer avec Google
                  </Button>
                </>}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
